import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse
} from '@mui/material';
import {
  Search,
  TrendingUp,
  Info,
  Refresh,
  ExpandMore,
  ExpandLess,
  Person,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { formatCurrency, formatPercent } from '../utils/formatters';
import UserDetailsModal from '../components/UserDetailsModal';

const Stakings = () => {
  const [stakings, setStakings] = useState([]);
  const [groupedUsers, setGroupedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Модальное окно пользователя
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);

  // Развернутые строки
  const [expandedRows, setExpandedRows] = useState({});

  // Сортировка
  const [sortField, setSortField] = useState('total_amount');
  const [sortOrder, setSortOrder] = useState('desc');

  // Фильтры
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [stats, setStats] = useState({
    totalStaked: 0,
    totalRewards: 0,
    activeStakings: 0,
    averageAPR: 0,
    uniqueUsers: 0
  });

  const loadStakings = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        sortBy: 'actual_amount',
        sortOrder: 'desc',
        limit: 1000
      };

      if (typeFilter !== 'all') {
        params.stakingType = typeFilter;
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await adminAPI.getStakings(params);

      if (response.data.success && response.data.data) {
        const stakingsData = Array.isArray(response.data.data)
          ? response.data.data
          : (response.data.data.stakings || []);
        setStakings(stakingsData);
      } else {
        setStakings([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки стейкингов:', error);
      setStakings([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    loadStakings();
  }, [loadStakings]);

  useEffect(() => {
    // Группируем стейкинги по пользователям
    if (stakings.length > 0) {
      const userMap = {};

      stakings.forEach(s => {
        const key = s.user_id;
        if (!userMap[key]) {
          userMap[key] = {
            user_id: s.user_id,
            username: s.username,
            first_name: s.first_name,
            last_name: s.last_name,
            telegram_id: s.telegram_id,
            stakings: [],
            total_amount: 0,
            total_visual: 0,
            total_earned: 0,
            total_daily_reward: 0,
            avg_apr: 0,
            staking_count: 0,
            active_count: 0,
            lunar_count: 0,
            star_count: 0
          };
        }

        userMap[key].stakings.push(s);
        userMap[key].total_amount += parseFloat(s.actual_amount || 0);
        userMap[key].total_visual += parseFloat(s.visual_balance || 0);
        userMap[key].total_earned += parseFloat(s.total_earned || 0);
        userMap[key].total_daily_reward += parseFloat(s.daily_reward || 0);
        userMap[key].staking_count++;
        if (s.status === 'active') userMap[key].active_count++;
        if (s.staking_type === 'lunar') userMap[key].lunar_count++;
        if (s.staking_type === 'star') userMap[key].star_count++;
      });

      // Вычисляем средний APR для каждого пользователя
      Object.values(userMap).forEach(user => {
        const activeStakings = user.stakings.filter(s => s.status === 'active');
        if (activeStakings.length > 0) {
          user.avg_apr = activeStakings.reduce((sum, s) => sum + parseFloat(s.current_apr || 0), 0) / activeStakings.length;
        }
      });

      // Сортируем по сумме стейкинга
      const sorted = Object.values(userMap).sort((a, b) => b.total_amount - a.total_amount);
      setGroupedUsers(sorted);

      // Обновляем статистику
      const total = stakings.reduce((sum, s) => sum + parseFloat(s.actual_amount || 0), 0);
      const rewards = stakings.reduce((sum, s) => sum + parseFloat(s.total_earned || 0), 0);
      const active = stakings.filter(s => s.status === 'active').length;
      const avgAPR = stakings.length > 0
        ? stakings.reduce((sum, s) => sum + parseFloat(s.current_apr || 0), 0) / stakings.length
        : 0;

      setStats({
        totalStaked: total,
        totalRewards: rewards,
        activeStakings: active,
        averageAPR: avgAPR,
        uniqueUsers: Object.keys(userMap).length
      });
    } else {
      setGroupedUsers([]);
      setStats({
        totalStaked: 0,
        totalRewards: 0,
        activeStakings: 0,
        averageAPR: 0,
        uniqueUsers: 0
      });
    }
  }, [stakings]);

  const handleUserClick = (userId) => {
    setSelectedUserId(userId);
    setUserModalOpen(true);
  };

  const toggleRowExpand = (userId) => {
    setExpandedRows(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  // Обработчик сортировки
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Компонент заголовка с сортировкой
  const SortableHeader = ({ field, children, align = 'left' }) => (
    <TableCell
      sx={{
        backgroundColor: '#1e293b',
        fontWeight: 'bold',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { backgroundColor: '#334155' }
      }}
      align={align}
      onClick={() => handleSort(field)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start', gap: 0.5 }}>
        {children}
        {sortField === field && (
          sortOrder === 'desc' ? <ArrowDownward sx={{ fontSize: 16 }} /> : <ArrowUpward sx={{ fontSize: 16 }} />
        )}
      </Box>
    </TableCell>
  );

  const getStakingTypeConfig = (type) => {
    const types = {
      'lunar': { label: 'Лунный', color: 'primary' },
      'star': { label: 'Звездный', color: 'warning' }
    };
    return types[type] || { label: type || 'Неизвестный', color: 'default' };
  };

  const getStatusConfig = (status) => {
    const statuses = {
      'active': { label: 'Активен', color: 'success' },
      'completed': { label: 'Завершен', color: 'default' },
      'burned': { label: 'Сожжен', color: 'error' },
      'paused': { label: 'Приостановлен', color: 'warning' }
    };
    return statuses[status] || { label: status || 'Неизвестно', color: 'default' };
  };

  // Фильтрация и сортировка
  const filteredUsers = groupedUsers
    .filter(user =>
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.telegram_id?.toString().includes(searchTerm)
    )
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Для строковых полей
      if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }

      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
      } else {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      }
    });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Стейкинги
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadStakings}
          disabled={loading}
        >
          Обновить
        </Button>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Всего в стейкинге
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatCurrency(stats.totalStaked)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={75}
                sx={{ backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: 'white' } }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Выплачено наград
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatCurrency(stats.totalRewards)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={60}
                sx={{ backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: 'white' } }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Уник. юзеров / Депозитов
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {stats.uniqueUsers} / {stats.activeStakings}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={85}
                sx={{ backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: 'white' } }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <CardContent>
              <Typography color="white" variant="body2">
                Средний APR
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                {formatPercent(stats.averageAPR)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(stats.averageAPR * 50, 100)}
                sx={{ backgroundColor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { backgroundColor: 'white' } }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Поиск и фильтры */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Поиск по имени пользователя..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 250, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Тип стейкинга</InputLabel>
            <Select
              value={typeFilter}
              label="Тип стейкинга"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">Все типы</MenuItem>
              <MenuItem value="lunar">Лунный</MenuItem>
              <MenuItem value="star">Звездный</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Статус</InputLabel>
            <Select
              value={statusFilter}
              label="Статус"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Все статусы</MenuItem>
              <MenuItem value="active">Активные</MenuItem>
              <MenuItem value="completed">Завершенные</MenuItem>
              <MenuItem value="burned">Сожженные</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Таблица пользователей со стейкингами */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#1e293b', width: 50 }}></TableCell>
                <SortableHeader field="username">Пользователь</SortableHeader>
                <SortableHeader field="staking_count" align="right">Депозитов</SortableHeader>
                <SortableHeader field="total_amount" align="right">Сумма</SortableHeader>
                <SortableHeader field="total_visual" align="right">Визуальный</SortableHeader>
                <TableCell sx={{ backgroundColor: '#1e293b', fontWeight: 'bold' }} align="center">Типы</TableCell>
                <SortableHeader field="avg_apr" align="right">Сред. APR</SortableHeader>
                <SortableHeader field="total_daily_reward" align="right">Ежедн. доход</SortableHeader>
                <SortableHeader field="total_earned" align="right">Заработано</SortableHeader>
                <TableCell sx={{ backgroundColor: '#1e293b', fontWeight: 'bold' }} align="center">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    Нет данных
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <React.Fragment key={user.user_id}>
                    {/* Основная строка пользователя */}
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' },
                        backgroundColor: expandedRows[user.user_id] ? 'rgba(99, 102, 241, 0.05)' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpand(user.user_id)}
                        >
                          {expandedRows[user.user_id] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell
                        onClick={() => handleUserClick(user.user_id)}
                        sx={{ cursor: 'pointer', '&:hover': { color: '#6366f1' } }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person sx={{ color: '#6366f1', fontSize: 20 }} />
                          <Box>
                            <Typography sx={{ fontWeight: 'medium' }}>
                              {user.username || [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Без имени'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {user.telegram_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${user.active_count}/${user.staking_count}`}
                          size="small"
                          color={user.active_count > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 'bold', color: '#6366f1' }}>
                          {formatCurrency(user.total_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ color: '#8b5cf6' }}>
                          {formatCurrency(user.total_visual)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          {user.lunar_count > 0 && (
                            <Chip label={`L: ${user.lunar_count}`} size="small" color="primary" variant="outlined" />
                          )}
                          {user.star_count > 0 && (
                            <Chip label={`S: ${user.star_count}`} size="small" color="warning" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <TrendingUp sx={{ mr: 0.5, color: '#10b981', fontSize: 18 }} />
                          <Typography sx={{ color: '#10b981', fontWeight: 'bold' }}>
                            {formatPercent(user.avg_apr)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ color: '#10b981' }}>
                          +{formatCurrency(user.total_daily_reward)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                          {formatCurrency(user.total_earned)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleUserClick(user.user_id)}
                          title="Открыть карточку пользователя"
                        >
                          <Info />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Развернутая секция с деталями стейкингов */}
                    <TableRow>
                      <TableCell colSpan={10} sx={{ p: 0, borderBottom: expandedRows[user.user_id] ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                        <Collapse in={expandedRows[user.user_id]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.2)' }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: '#9ca3af' }}>
                              Детали стейкингов ({user.staking_count}):
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Тип</TableCell>
                                  <TableCell align="right">Сумма</TableCell>
                                  <TableCell align="right">APR</TableCell>
                                  <TableCell align="right">Заработано</TableCell>
                                  <TableCell>Статус</TableCell>
                                  <TableCell>Дней</TableCell>
                                  <TableCell>Создан</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {user.stakings.map((s) => (
                                  <TableRow key={s.id}>
                                    <TableCell>{s.id}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={getStakingTypeConfig(s.staking_type).label}
                                        size="small"
                                        color={getStakingTypeConfig(s.staking_type).color}
                                      />
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(s.actual_amount)}</TableCell>
                                    <TableCell align="right" sx={{ color: '#10b981' }}>
                                      {formatPercent(s.current_apr)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#f59e0b' }}>
                                      {formatCurrency(s.total_earned)}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={getStatusConfig(s.status).label}
                                        size="small"
                                        color={getStatusConfig(s.status).color}
                                      />
                                    </TableCell>
                                    <TableCell>{s.days_active || 0}</TableCell>
                                    <TableCell>
                                      {s.created_at ? new Date(s.created_at).toLocaleDateString('ru-RU') : 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Typography variant="body2" color="textSecondary">
            Показано {filteredUsers.length} пользователей из {stats.uniqueUsers} |
            Всего депозитов: {stakings.length}
          </Typography>
        </Box>
      </Paper>

      {/* Модальное окно карточки пользователя */}
      <UserDetailsModal
        open={userModalOpen}
        onClose={() => {
          setUserModalOpen(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
      />
    </Box>
  );
};

export default Stakings;
